import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'

import type { InventoryFilters } from '@/lib/api/client/inventory'
import { getShowcaseFormula } from '@/lib/api/client/ymarket-pricing'
import {
  MarketplaceBindingSchema,
  MissingTaxonomyFacetSchema,
  TaxonomyFactSchema,
  type AvailableFilters,
  type InventoryDiscrepancyReason,
  type LocalStockState,
  type MarketplaceBinding,
  type MissingTaxonomyFacet,
  type ProviderSlot,
  type TaxonomyFact,
  type UnitLifecycleScope,
} from '@/lib/api/schemas/inventory.schema'
import { YmPriceAttractivenessLevelSchema, type YmEconomicsFilter } from '@/lib/api/schemas/ymarket-pricing.schema'
import { ATTRACTIVENESS_LABELS } from '@/lib/warehouse/ymarket-showcase'
import {
  fineness as finenessLabel,
  matchBucketLabels,
  materialLabel,
  productTypeLabel,
  shapeLabel,
  storefrontLabel,
  warehouseProductStatusLabels,
} from '@/lib/warehouse/labels'
import {
  STATUS_SCOPES,
  STOREFRONT_CODES,
  isWarehousePresetActive,
  panelCountKey,
  parseMember,
  statusPanelCountKey,
  statusScopeConflict,
  statusScopeOf,
  storefrontPanelCountKey,
  storefrontStateOf,
  warehousePresetsFor,
  withStatusScope,
  withStorefrontState,
  type StatusScope,
  type StorefrontCode,
  type StorefrontState,
  type WarehouseGrouping,
} from '@/lib/warehouse/url-state'
import type { ViewPresetKey } from '@/lib/views/presets'
import { StorefrontMark } from '@/components/warehouse/storefronts'
import { STATUSES, STATUS_LABELS } from '@/lib/order-helpers'

const localStockScopeLabels: Record<LocalStockState, string> = {
  in_stock: warehouseProductStatusLabels.in_stock,
  out_of_stock: 'Нет на складе',
  unknown: 'Остаток неизвестен',
}

const lifecycleChipLabels: Record<Exclude<UnitLifecycleScope, 'all'>, string> = {
  in_stock: warehouseProductStatusLabels.in_stock,
  in_transit: warehouseProductStatusLabels.in_transit,
  sold: warehouseProductStatusLabels.sold,
}

const statusScopeLabels: Record<StatusScope, string> = {
  all: 'Все статусы',
  ...lifecycleChipLabels,
  out_of_stock: localStockScopeLabels.out_of_stock,
  unknown: localStockScopeLabels.unknown,
}

/** «Связь с площадкой»: one value per piece; «нет» is the chip «Не связано с площадкой». */
export const bindingLabels: Record<MarketplaceBinding, string> = {
  bound: 'есть',
  missing: 'нет',
  conflicting: 'спорная',
}

const MISSING_BINDING_LABEL = 'Не связано с площадкой'

/** «Не заполнено»: which recorded jewelry fact is missing. */
export const missingFactLabels: Record<TaxonomyFact, string> = {
  productType: 'тип',
  shape: 'форма',
  material: 'металл',
  fineness: 'проба',
}

const FACT_COUNT_WORDS: Readonly<Record<number, string>> = { 2: 'двух', 3: 'трёх', 4: 'четырёх' }

function missingTaxonomyLabel(value: MissingTaxonomyFacet, recorded: number): string {
  return value === 'any' ? `любое из ${FACT_COUNT_WORDS[recorded] ?? String(recorded)}` : missingFactLabels[value]
}

/**
 * A fact no piece records is a gap on no piece, so it is not offered and «любое»
 * does not count it; an older server that names nothing offers all four.
 */
function recordedFacts(available: AvailableFilters | null): readonly TaxonomyFact[] {
  return available?.recordedTaxonomy ?? TaxonomyFactSchema.options
}

const SHOWCASE_PER_GRAM_LABELS = {
  ymarketShowcasePerGramMin: 'Витрина Маркета, ₽/г — от',
  ymarketShowcasePerGramMax: 'Витрина Маркета, ₽/г — до',
} as const

const CHEAP_THRESHOLD_KEY = 'cheapThreshold'
const BELOW_THRESHOLD_PANEL_LABEL = 'Витрина ниже порога металла'

export interface MetalThreshold {
  metal: string
  value: number
}

/** «Маркет: ниже порога»: the numbers come from the formula in force when it depends on the metal alone. */
export function belowThresholdLabel(byMetal: readonly MetalThreshold[] | null): string {
  const gold = byMetal?.find((entry) => entry.metal === 'gold')
  const silver = byMetal?.find((entry) => entry.metal === 'silver')
  if (gold === undefined || silver === undefined) return 'Витрина ниже порога по формуле «Порог дешёвой витрины»'
  const perGram = (value: number) => value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })
  return `Витрина ниже порога: золото < ${perGram(gold.value)}, серебро < ${perGram(silver.value)} ₽/г`
}

function useCheapThresholds(active: boolean): MetalThreshold[] | null {
  const [thresholds, setThresholds] = useState<MetalThreshold[] | null>(null)
  useEffect(() => {
    if (!active) return undefined
    const controller = new AbortController()
    getShowcaseFormula(controller.signal)
      .then((formula) => setThresholds(formula.formulas.find((definition) => definition.key === CHEAP_THRESHOLD_KEY)?.byMetal ?? null))
      .catch(() => { if (!controller.signal.aborted) setThresholds(null) })
    return () => controller.abort()
  }, [active])
  return thresholds
}

function showcaseRangeLabel(low: string | undefined, high: string | undefined): string {
  const rub = (value: string) => `${Number(value).toLocaleString('ru-RU')} ₽`
  if (low && high) return `Покупатель заплатит: ${rub(low)} – ${rub(high)}`
  return low ? `Покупатель заплатит от ${rub(low)}` : `Покупатель заплатит до ${rub(high ?? '0')}`
}

const ymarketChipLabels: Record<YmEconomicsFilter, string> = {
  below_floor: 'Ниже порога ₽/г',
  in_promo: 'В акции',
  boosted: 'С бустом',
  no_cost: 'Нет закупки',
  stale: 'Цены Маркета устарели',
  outliers: 'Выброс по ₽/г',
  drift: 'Цена расходится с Маркетом',
  forge_price_not_set: 'Наша цена для Маркета не задана',
}

// Values the panel no longer offers; a saved link still shows them, in Russian, so they can be removed.
const discrepancyChipLabels: Record<InventoryDiscrepancyReason, string> = {
  missing_binding: MISSING_BINDING_LABEL,
  conflicting_binding: 'Спорная привязка к площадке',
  price_divergence: 'Цена 1С расходится с площадкой',
  stock_divergence: 'Остатки расходятся',
  stale_observation: 'Данные площадок устарели',
  synchronization_failure: 'Ошибка синхронизации площадки',
  recent_movement: 'Недавнее движение',
}

const storefrontStateLabels: Record<Exclude<StorefrontState, 'any'>, string> = {
  present: 'есть',
  absent: 'нет',
  ready: 'готово к заливке',
}

/** The server's `panelCounts`, keyed as `panelCountKey` names each value. */
export type PanelCounts = Readonly<Record<string, number | undefined>>

/** «Заказы» counts orders in its header, so every count there names its unit. */
interface CountUnits { pieces: string; orders: string }
const NO_UNITS: CountUnits = { pieces: '', orders: '' }
const ORDER_VIEW_UNITS: CountUnits = { pieces: ' изд.', orders: ' зак.' }

function withCount(label: string, count: number | undefined, unit = ''): string {
  return count === undefined ? label : `${label} · ${count.toLocaleString('ru-RU')}${unit}`
}

function countOf(counts: PanelCounts, filters: InventoryFilters): number | undefined {
  const key = panelCountKey(filters)
  return key === null ? undefined : counts[key]
}

interface Props {
  filters: InventoryFilters
  availableFilters: AvailableFilters | null
  providerSlots: readonly ProviderSlot[] | null
  lifecycle: UnitLifecycleScope
  open: boolean
  triggerRef: RefObject<HTMLButtonElement | null>
  onClose: () => void
  /** The one «Статус» control writes the status and the stock scope together. */
  onLifecycle: (value: UnitLifecycleScope, filters: InventoryFilters) => void
  onChange: (next: InventoryFilters) => void
  onTogglePreset: (presetId: string) => void
  onReset: () => void
  readyCounts?: Readonly<Record<string, number>>
  view?: ViewPresetKey
  grouping?: WarehouseGrouping
  /** Counts of the chips and storefront states, read for the current filters. */
  counts?: PanelCounts
  /** «Найдено: N …» of the current filters, or null while it is unknown. */
  resultLabel?: string | null
}

interface Option { value: string; label: string }

function Select({ label, value, options, onChange, emptyLabel = 'Все', unavailable = false }: {
  label: string
  value: string | undefined
  options: readonly Option[]
  onChange: (value: string | undefined) => void
  emptyLabel?: string | null
  /** The server named no value for this population: the field says so instead of offering «Все» alone. */
  unavailable?: boolean
}) {
  return (
    <label className="block text-[11px] text-ink-muted">
      {label}
      <select
        aria-label={label}
        value={unavailable ? '' : value ?? ''}
        disabled={unavailable}
        onChange={(event) => onChange(event.target.value || undefined)}
        className="mt-1 h-9 w-full rounded-md border border-rule bg-white px-2 text-xs text-ink disabled:cursor-not-allowed disabled:bg-bone/50 disabled:text-ink-muted"
      >
        {unavailable ? <option value="">нет данных</option> : <>
          {emptyLabel === null ? null : <option value="">{emptyLabel}</option>}
          {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </>}
      </select>
    </label>
  )
}

type FacetName = 'material' | 'fineness' | 'productType' | 'shape' | 'techSize' | 'manufacturer'

/** Values with their counts; a chosen value the population no longer carries stays offered at 0. */
function facetOptions(
  available: AvailableFilters | null,
  facet: FacetName,
  values: readonly string[] | null | undefined,
  chosen: string | undefined,
  label: (value: string) => string,
  unit: string,
): { options: Option[]; unavailable: boolean } {
  const offered = [...(values ?? [])]
  if (chosen !== undefined && !offered.includes(chosen)) offered.push(chosen)
  const counts = available?.counts[facet]
  return {
    options: offered.map((value) => ({ value, label: withCount(label(value), counts === undefined ? undefined : counts[value] ?? 0, unit) })),
    unavailable: available !== null && offered.length === 0,
  }
}

function FacetSelect({ label, facet, values, available, value, format = (text) => text, unit, onChange }: {
  label: string
  facet: FacetName
  values: readonly string[] | null | undefined
  available: AvailableFilters | null
  value: string | undefined
  format?: (value: string) => string
  unit: string
  onChange: (value: string | undefined) => void
}) {
  const { options, unavailable } = facetOptions(available, facet, values, value, format, unit)
  return <Select label={label} value={value} options={options} unavailable={unavailable} onChange={onChange} />
}

function MissingTaxonomySelect({ value, available, counts, onChange }: {
  value: MissingTaxonomyFacet | undefined
  available: AvailableFilters | null
  counts: PanelCounts
  onChange: (value: MissingTaxonomyFacet | undefined) => void
}) {
  const recorded = recordedFacts(available)
  const offered = MissingTaxonomyFacetSchema.options.filter((facet) => (
    facet === value || (facet === 'any' ? recorded.length >= 2 : recorded.includes(facet))
  ))
  return (
    <Select
      label="Не заполнено"
      value={value}
      unavailable={offered.length === 0}
      options={offered.map((facet) => ({ value: facet, label: withCount(missingTaxonomyLabel(facet, recorded.length), countOf(counts, { missingTaxonomy: facet })) }))}
      onChange={(next) => onChange(parseMember(MissingTaxonomyFacetSchema, next))}
    />
  )
}

function ToggleChip({ pressed, onClick, children, title, disabled = false }: { pressed: boolean; onClick: () => void; children: ReactNode; title?: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${pressed ? 'border-gold-deep bg-gold/10 text-gold-deep' : 'border-rule text-ink-2 hover:bg-bone/60'}`}
    >
      {children}
    </button>
  )
}

function PresetChips({ group, filters, view, counts, onTogglePreset }: {
  group: 'daily' | 'problems'
  filters: InventoryFilters
  view: ViewPresetKey
  counts: PanelCounts
  onTogglePreset: (presetId: string) => void
}) {
  return <>
    {warehousePresetsFor(view).filter((preset) => preset.group === group).map((preset) => (
      <ToggleChip key={preset.id} pressed={isWarehousePresetActive(filters, preset)} title={preset.description} onClick={() => onTogglePreset(preset.id)}>
        {withCount(preset.label, countOf(counts, preset.filters))}
      </ToggleChip>
    ))}
  </>
}

interface StorefrontRow {
  code: StorefrontCode
  connected: boolean
  placementObserved: boolean
}

function storefrontRows(providerSlots: readonly ProviderSlot[] | null): StorefrontRow[] {
  return STOREFRONT_CODES.map((code) => {
    const slots = (providerSlots ?? []).filter((slot) => slot.providerCode === code)
    return {
      code,
      connected: slots.some((slot) => slot.connectionState === 'connected'),
      placementObserved: slots.some((slot) => slot.connectionState === 'connected' && slot.placementObserved),
    }
  })
}

const PUBLISH_TARGETS: readonly StorefrontCode[] = ['wildberries', 'ymarket', 'ozon']

/** One «Площадки» picker: per storefront «есть», «нет» or «готово к заливке»; several storefronts combine with AND. */
function StorefrontPicker({ filters, providerSlots, readyCounts, counts, unit, onChange }: {
  filters: InventoryFilters
  providerSlots: readonly ProviderSlot[] | null
  readyCounts: Readonly<Record<string, number>>
  counts: PanelCounts
  unit: string
  onChange: (next: InventoryFilters) => void
}) {
  const rows = storefrontRows(providerSlots)
  const connected = rows.filter((row) => row.connected)
  const disconnected = rows.filter((row) => !row.connected && storefrontStateOf(filters, row.code) === 'any')
  return (
    <div className="grid gap-2">
      {rows.filter((row) => row.connected || storefrontStateOf(filters, row.code) !== 'any').map((row) => {
        const state = storefrontStateOf(filters, row.code)
        const name = storefrontLabel(row.code)
        const noPlacement = `Нет данных о размещении на ${name}: без них отбор выбрал бы все изделия.`
        const pick = (next: StorefrontState) => onChange(withStorefrontState(filters, row.code, state === next ? 'any' : next))
        return (
          <div key={row.code} role="group" aria-label={`Площадка ${name}`} className="flex flex-wrap items-center gap-2">
            <span className="flex w-36 items-center gap-2 text-xs text-ink"><StorefrontMark providerCode={row.code} />{name}</span>
            {(['present', 'absent'] as const).map((choice) => (
              <ToggleChip
                key={choice}
                pressed={state === choice}
                disabled={!row.placementObserved && state !== choice}
                title={row.placementObserved ? undefined : noPlacement}
                onClick={() => pick(choice)}
              >
                {withCount(storefrontStateLabels[choice], row.placementObserved ? counts[storefrontPanelCountKey(row.code, choice)] : undefined, unit)}
              </ToggleChip>
            ))}
            {PUBLISH_TARGETS.includes(row.code) ? (
              <ToggleChip pressed={state === 'ready'} onClick={() => pick('ready')}>
                {withCount(storefrontStateLabels.ready, readyCounts[row.code] ?? 0, unit)}
              </ToggleChip>
            ) : null}
          </div>
        )
      })}
      {connected.length === 0 ? <p className="text-xs text-ink-muted">Ни одна площадка не подключена.</p> : null}
      {disconnected.length > 0 ? (
        <p data-testid="storefronts-not-connected" className="text-[11px] text-ink-muted">
          Не подключено: {disconnected.map((row) => storefrontLabel(row.code)).join(', ')}
        </p>
      ) : null}
      {filters.availableOn !== undefined && filters.availableOn.length > 1 ? (
        <p className="text-[11px] text-ink-muted">Из старой ссылки: есть хотя бы на одной из {filters.availableOn.map(storefrontLabel).join(', ')}.</p>
      ) : null}
      <p className="text-[11px] text-ink-muted">Несколько площадок — изделия, для которых выполнены все условия сразу.</p>
    </div>
  )
}

export function WarehouseFilters({ filters, availableFilters, providerSlots, lifecycle, open, triggerRef, onClose, onLifecycle, onChange, onTogglePreset, onReset, readyCounts = {}, view = 'actual', grouping = 'none', counts = {}, resultLabel = null }: Props) {
  const surfaceRef = useRef<HTMLDivElement | null>(null)
  const closeSurface = useRef(onClose)
  closeSurface.current = onClose

  useEffect(() => {
    if (!open) return
    const surface = surfaceRef.current
    const close = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      event.preventDefault()
      closeSurface.current()
    }
    const outside = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node) || surface?.contains(target) || triggerRef.current?.contains(target)) return
      closeSurface.current()
    }
    window.addEventListener('keydown', close)
    document.addEventListener('pointerdown', outside)
    // Focus starts on «Закрыть фильтры», never on «Сбросить фильтры», so Enter cannot wipe the filters.
    const frame = requestAnimationFrame(() => surface?.querySelector<HTMLElement>('button[aria-label="Закрыть фильтры"]')?.focus())
    return () => {
      window.removeEventListener('keydown', close)
      document.removeEventListener('pointerdown', outside)
      cancelAnimationFrame(frame)
      if (document.activeElement === document.body || surface?.contains(document.activeElement)) triggerRef.current?.focus()
    }
  }, [open, triggerRef])

  const set = (patch: Partial<InventoryFilters>) => onChange({ ...filters, ...patch })
  const warehouseView = view !== 'orders'
  const units = warehouseView ? NO_UNITS : ORDER_VIEW_UNITS

  if (!open) return null

  return createPortal(
    <section
      ref={surfaceRef}
      id="warehouse-filter-surface"
      role="dialog"
      aria-modal="false"
      aria-labelledby="warehouse-filter-title"
      className="fixed inset-x-3 top-3 z-50 flex max-h-[calc(100dvh-1.5rem)] flex-col rounded-lg border border-rule bg-paper shadow-[0_18px_60px_rgba(20,17,13,0.22)] sm:left-auto sm:right-5 sm:top-20 sm:w-[min(760px,calc(100vw-2.5rem))] sm:max-h-[calc(100dvh-6rem)]"
    >
      <header className="flex items-center justify-between gap-3 border-b border-rule px-3 py-2">
        <h2 id="warehouse-filter-title" className="text-sm font-semibold text-ink">Фильтры склада</h2>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onReset} className="rounded-full border border-rule px-3 py-1.5 text-xs text-ink-muted hover:text-ink">Сбросить фильтры</button>
          <button type="button" aria-label="Закрыть фильтры" onClick={onClose} className="size-8 rounded-md text-lg text-ink-muted hover:bg-bone/60">×</button>
        </div>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {warehouseView ? null : <p className="mb-2 text-[11px] text-ink-muted">Числа рядом со значениями: изд. — изделия, зак. — заказы.</p>}
        {warehouseView ? (
          <div role="group" aria-label="Каждый день" className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-ink">Каждый день</span>
            <PresetChips group="daily" filters={filters} view={view} counts={counts} onTogglePreset={onTogglePreset} />
          </div>
        ) : null}

        <fieldset className="mt-3 rounded-md border border-rule p-3">
          <legend className="px-1 text-xs font-semibold text-ink">Изделие</legend>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Select
              label={view === 'orders' ? 'Статус изделия' : 'Статус'}
              value={statusScopeOf(filters, lifecycle)}
              emptyLabel={null}
              options={STATUS_SCOPES.map((value) => ({ value, label: withCount(statusScopeLabels[value], counts[statusPanelCountKey(value)], units.pieces) }))}
              onChange={(value) => {
                const scope = STATUS_SCOPES.find((candidate) => candidate === value) ?? 'all'
                const next = withStatusScope(filters, scope)
                onLifecycle(next.lifecycle, next.filters)
              }}
            />
            <FacetSelect label="Материал" facet="material" values={availableFilters?.materials} available={availableFilters} value={filters.material} format={materialLabel} unit={units.pieces} onChange={(value) => set({ material: value })} />
            <FacetSelect label="Проба" facet="fineness" values={availableFilters?.finenesses} available={availableFilters} value={filters.fineness} format={finenessLabel} unit={units.pieces} onChange={(value) => set({ fineness: value })} />
            <FacetSelect label="Тип изделия" facet="productType" values={availableFilters?.productTypes} available={availableFilters} value={filters.productType} format={productTypeLabel} unit={units.pieces} onChange={(value) => set({ productType: value })} />
            <FacetSelect label="Форма" facet="shape" values={availableFilters?.shapes} available={availableFilters} value={filters.shape} format={shapeLabel} unit={units.pieces} onChange={(value) => set({ shape: value })} />
            <FacetSelect label="Размер" facet="techSize" values={availableFilters?.techSizes} available={availableFilters} value={filters.techSize} unit={units.pieces} onChange={(value) => set({ techSize: value })} />
            <FacetSelect label="Производитель" facet="manufacturer" values={availableFilters?.manufacturers} available={availableFilters} value={filters.manufacturer} unit={units.pieces} onChange={(value) => set({ manufacturer: value })} />
          </div>
        </fieldset>

        <fieldset className="mt-3 rounded-md border border-rule p-3">
          <legend className="px-1 text-xs font-semibold text-ink">Маркет</legend>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(['ymarketShowcaseMin', 'ymarketShowcaseMax'] as const).map((key) => (
              <label key={key} className="block text-[11px] text-ink-muted" title="Цена на витрине Маркета без Яндекс Пэй, целые рубли">
                {key === 'ymarketShowcaseMin' ? 'Покупатель заплатит от, ₽' : 'Покупатель заплатит до, ₽'}
                <input
                  aria-label={key === 'ymarketShowcaseMin' ? 'Покупатель заплатит от, ₽' : 'Покупатель заплатит до, ₽'}
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  value={filters[key] ?? ''}
                  onChange={(event) => {
                    const digits = event.target.value.replace(/\D/g, '').slice(0, 9)
                    set({ [key]: digits || undefined })
                  }}
                  className="mt-1 h-9 w-full rounded-md border border-rule bg-white px-2 text-xs tabular-nums text-ink"
                />
              </label>
            ))}
            <label className="block text-[11px] text-ink-muted" title="«Привлекательность цен» Маркета: наша цена против границ отчёта «Цены»">
              Цена на Маркете
              <select
                aria-label="Цена на Маркете"
                value={filters.ymarketAttractiveness ?? ''}
                onChange={(event) => {
                  const level = YmPriceAttractivenessLevelSchema.safeParse(event.target.value)
                  set({ ymarketAttractiveness: level.success ? level.data : undefined })
                }}
                className="mt-1 h-9 w-full rounded-md border border-rule bg-white px-2 text-xs text-ink"
              >
                <option value="">Любая</option>
                {YmPriceAttractivenessLevelSchema.options.map((level) => <option key={level} value={level}>{ATTRACTIVENESS_LABELS[level]}</option>)}
              </select>
            </label>
            <label className="flex h-9 items-center gap-2 self-end rounded-md border border-rule bg-white px-2 text-xs" title="Витрина без Яндекс Пэй за грамм ниже порога металла из формулы «Порог дешёвой витрины»; изделие без металла не попадает">
              <input type="checkbox" checked={filters.ymarketBelowThreshold === true} onChange={(event) => set({ ymarketBelowThreshold: event.target.checked || undefined })} />
              {BELOW_THRESHOLD_PANEL_LABEL}
            </label>
            {(['ymarketShowcasePerGramMin', 'ymarketShowcasePerGramMax'] as const).map((key) => (
              <label key={key} className="block text-[11px] text-ink-muted" title="Цена на витрине Маркета без Яндекс Пэй за грамм веса из карточки">
                {SHOWCASE_PER_GRAM_LABELS[key]}
                <input
                  aria-label={SHOWCASE_PER_GRAM_LABELS[key]}
                  type="number"
                  min={0}
                  step={100}
                  inputMode="decimal"
                  value={filters[key] ?? ''}
                  onChange={(event) => set({ [key]: event.target.value === '' ? undefined : Number(event.target.value) })}
                  className="mt-1 h-9 w-full rounded-md border border-rule bg-white px-2 text-xs tabular-nums text-ink"
                />
              </label>
            ))}
          </div>
        </fieldset>

        {grouping === 'order' ? <fieldset className="mt-3 rounded-md border border-rule p-3">
          <legend className="px-1 text-xs font-semibold text-ink">Заказ</legend>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <fieldset>
              <legend className="mb-1 text-[11px] text-ink-muted">Программа заказа</legend>
              <div className="flex flex-wrap gap-1">
                {(['DBS', 'FBS'] as const).map((program) => {
                  const selected = filters.orderProgram?.includes(program) ?? false
                  return (
                    <button
                      key={program}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => {
                        const next = new Set(filters.orderProgram ?? [])
                        if (selected) next.delete(program)
                        else next.add(program)
                        set({ orderProgram: next.size > 0 ? [...next] : undefined })
                      }}
                      className={`rounded-full border px-2.5 py-1.5 text-[11px] ${selected ? 'border-ink bg-bone text-ink' : 'border-rule text-ink-muted'}`}
                    >
                      {withCount(program, countOf(counts, { orderProgram: [program] }), units.pieces)}
                    </button>
                  )
                })}
              </div>
            </fieldset>
            <label className="block text-[11px] text-ink-muted">
              Город заказа
              <input aria-label="Город заказа" value={filters.orderCity ?? ''} onChange={(event) => set({ orderCity: event.target.value || undefined })} className="mt-1 h-9 w-full rounded-md border border-rule bg-white px-2 text-xs text-ink" />
            </label>
            <fieldset className="lg:col-span-3">
              <legend className="mb-1 text-[11px] text-ink-muted">Статус заказа</legend>
              <div className="flex flex-wrap gap-1">
                {orderStatusOptions(availableFilters?.orderStatuses, filters.orderStatus).map((status) => {
                  const selected = filters.orderStatus?.includes(status.key) ?? false
                  const count = availableFilters?.counts.orderStatus?.[status.key]
                  return <button key={status.key} type="button" aria-pressed={selected} onClick={() => {
                    const next = new Set(filters.orderStatus ?? [])
                    if (selected) next.delete(status.key)
                    else next.add(status.key)
                    set({ orderStatus: next.size > 0 ? [...next] : undefined })
                  }} className={`rounded-full border px-2.5 py-1.5 text-[11px] ${selected ? 'border-ink bg-bone text-ink' : 'border-rule text-ink-muted'}`}>{withCount(status.label, count, units.orders)}</button>
                })}
              </div>
            </fieldset>
          </div>
        </fieldset> : null}

        <fieldset className="mt-3 rounded-md border border-rule p-3">
          <legend className="px-1 text-xs font-semibold text-ink">Площадки</legend>
          <StorefrontPicker filters={filters} providerSlots={providerSlots} readyCounts={readyCounts} counts={counts} unit={units.pieces} onChange={onChange} />
        </fieldset>

        {warehouseView ? <fieldset className="mt-3 rounded-md border border-rule p-3">
          <legend className="px-1 text-xs font-semibold text-ink">Экономика Маркета</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-[11px] text-ink-muted">На руки, ₽/г — от
              <input aria-label="На руки, ₽/г — от" type="number" min="0" step="0.01" value={filters.ymarketNetPerGramMin ?? ''} onChange={(event) => set({ ymarketNetPerGramMin: event.target.value === '' ? undefined : Number(event.target.value) })} className="mt-1 h-9 w-full rounded-md border border-rule bg-white px-2 text-xs text-ink" />
            </label>
            <label className="block text-[11px] text-ink-muted">На руки, ₽/г — до
              <input aria-label="На руки, ₽/г — до" type="number" min="0" step="0.01" value={filters.ymarketNetPerGramMax ?? ''} onChange={(event) => set({ ymarketNetPerGramMax: event.target.value === '' ? undefined : Number(event.target.value) })} className="mt-1 h-9 w-full rounded-md border border-rule bg-white px-2 text-xs text-ink" />
            </label>
            <label className="block text-[11px] text-ink-muted">Цена для цели — от
              <input aria-label="Цена для цели — от" type="number" min="0" step="100" value={filters.ymarketPriceForTargetMin ?? ''} onChange={(event) => set({ ymarketPriceForTargetMin: event.target.value === '' ? undefined : Number(event.target.value) })} className="mt-1 h-9 w-full rounded-md border border-rule bg-white px-2 text-xs text-ink" />
            </label>
            <label className="block text-[11px] text-ink-muted">Цена для цели — до
              <input aria-label="Цена для цели — до" type="number" min="0" step="100" value={filters.ymarketPriceForTargetMax ?? ''} onChange={(event) => set({ ymarketPriceForTargetMax: event.target.value === '' ? undefined : Number(event.target.value) })} className="mt-1 h-9 w-full rounded-md border border-rule bg-white px-2 text-xs text-ink" />
            </label>
          </div>
        </fieldset> : null}

        {warehouseView ? <fieldset className="mt-3 rounded-md border border-rule p-3">
          <legend className="px-1 text-xs font-semibold text-ink">Проблемы</legend>
          <div className="flex flex-wrap gap-2">
            <PresetChips group="problems" filters={filters} view={view} counts={counts} onTogglePreset={onTogglePreset} />
          </div>
          <p className="mt-2 text-[11px] text-ink-muted">Несколько проблем — изделия, у которых есть все выбранные.</p>
        </fieldset> : null}

        {warehouseView ? <details className="mt-3 rounded-md border border-rule p-3">
          <summary className="cursor-pointer text-xs font-semibold text-ink">Для сверки</summary>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Select
              label="Связь с площадкой"
              value={filters.binding}
              options={MarketplaceBindingSchema.options.map((value) => ({ value, label: withCount(bindingLabels[value], countOf(counts, { binding: value })) }))}
              onChange={(value) => set({ binding: parseMember(MarketplaceBindingSchema, value) })}
            />
            <MissingTaxonomySelect value={filters.missingTaxonomy} available={availableFilters} counts={counts} onChange={(missingTaxonomy) => set({ missingTaxonomy })} />
            <label className="flex h-9 items-center gap-2 self-end rounded-md border border-rule bg-white px-2 text-xs">
              <input type="checkbox" checked={filters.missingMarking === true} onChange={(event) => set({ missingMarking: event.target.checked || undefined })} />
              Без маркировки
            </label>
            <label className="flex h-9 items-center gap-2 self-end rounded-md border border-rule bg-white px-2 text-xs">
              <input type="checkbox" checked={filters.unavailableOrStaleStock === true} onChange={(event) => set({ unavailableOrStaleStock: event.target.checked || undefined })} />
              Остаток 1С устарел или не записан
            </label>
          </div>
        </details> : null}
      </div>
      <footer data-testid="warehouse-filter-footer" className="flex items-center justify-between gap-3 border-t border-rule bg-paper px-3 py-2">
        <output aria-live="polite" className="text-xs font-medium text-ink">{resultLabel ?? 'Считаем…'}</output>
        <button type="button" onClick={onClose} className="btn btn-sm bg-gold text-ivory hover:bg-gold-deep">Показать</button>
      </footer>
    </section>,
    document.body,
  )
}

/** The statuses the orders carry, in the order-list order; a chosen one stays offered. */
function orderStatusOptions(served: readonly string[] | null | undefined, chosen: readonly string[] | undefined): { key: string; label: string }[] {
  if (served === null || served === undefined) return STATUSES
  const wanted = new Set([...served, ...(chosen ?? [])])
  const known = STATUSES.filter((status) => wanted.has(status.key))
  const other = [...wanted].filter((key) => !STATUSES.some((status) => status.key === key)).sort()
  return [...known, ...other.map((key) => ({ key, label: STATUS_LABELS[key] ?? key }))]
}

export interface FilterChip {
  label: string
  clear: () => InventoryFilters
  /** Set when the chip is the published-status scope, cleared through `onLifecycle`. */
  lifecycle?: true
}

/** Every chip the filters show; the «Фильтры N» badge is their count. */
export function warehouseFilterChips(filters: InventoryFilters, lifecycle: UnitLifecycleScope = 'all', thresholds: readonly MetalThreshold[] | null = null): FilterChip[] {
  const chips: FilterChip[] = []
  const without = (patch: Partial<InventoryFilters>) => () => ({ ...filters, ...patch })
  const push = (label: string, clear: () => InventoryFilters) => chips.push({ label, clear })
  if (filters.material) push(`Материал: ${materialLabel(filters.material)}`, without({ material: undefined }))
  if (filters.fineness) push(`Проба: ${finenessLabel(filters.fineness)}`, without({ fineness: undefined }))
  if (filters.productType) push(`Тип: ${productTypeLabel(filters.productType)}`, without({ productType: undefined }))
  if (filters.shape) push(`Форма: ${shapeLabel(filters.shape)}`, without({ shape: undefined }))
  if (filters.techSize) push(`Размер: ${filters.techSize}`, without({ techSize: undefined }))
  if (filters.manufacturer) push(`Производитель: ${filters.manufacturer}`, without({ manufacturer: undefined }))
  if (filters.localStock) push(`Статус: ${localStockScopeLabels[filters.localStock]}`, without({ localStock: undefined }))
  if (lifecycle !== 'all') chips.push({ label: `Статус: ${lifecycleChipLabels[lifecycle]}`, clear: () => filters, lifecycle: true })
  if (filters.availableOn !== undefined && filters.availableOn.length > 0) {
    push(`Есть хотя бы на одной: ${filters.availableOn.map(storefrontLabel).join(', ')}`, without({ availableOn: undefined }))
  }
  for (const code of STOREFRONT_CODES) {
    const state = storefrontStateOf(filters, code)
    if (state !== 'any') push(`${storefrontLabel(code)}: ${storefrontStateLabels[state]}`, () => withStorefrontState(filters, code, 'any'))
  }
  for (const [key, values] of [['presentOn', filters.presentOn], ['absentOn', filters.absentOn], ['readyFor', filters.readyFor]] as const) {
    for (const code of values ?? []) {
      if (STOREFRONT_CODES.some((known) => known === code)) continue
      push(`${storefrontLabel(code)}: ${storefrontStateLabels[key === 'presentOn' ? 'present' : key === 'absentOn' ? 'absent' : 'ready']}`, () => ({ ...filters, [key]: (values ?? []).filter((value) => value !== code) }))
    }
  }
  if (filters.withoutPhotos) push('Без фото', without({ withoutPhotos: undefined }))
  if (filters.ymarketEconomics) push(ymarketChipLabels[filters.ymarketEconomics], without({ ymarketEconomics: undefined }))
  for (const [key, label] of [
    ['ymarketNetPerGramMin', 'На руки, ₽/г — от'],
    ['ymarketNetPerGramMax', 'На руки, ₽/г — до'],
    ['ymarketPriceForTargetMin', 'Цена для цели — от'],
    ['ymarketPriceForTargetMax', 'Цена для цели — до'],
    ['ymarketShowcasePerGramMin', SHOWCASE_PER_GRAM_LABELS.ymarketShowcasePerGramMin],
    ['ymarketShowcasePerGramMax', SHOWCASE_PER_GRAM_LABELS.ymarketShowcasePerGramMax],
  ] as const) {
    const value = filters[key]
    if (value !== undefined) push(`${label}: ${value.toLocaleString('ru-RU')}`, without({ [key]: undefined }))
  }
  if (filters.ymarketAttractiveness) push(`Цена на Маркете: ${ATTRACTIVENESS_LABELS[filters.ymarketAttractiveness]}`, without({ ymarketAttractiveness: undefined }))
  if (filters.ymarketBelowThreshold) push(belowThresholdLabel(thresholds), without({ ymarketBelowThreshold: undefined }))
  if (filters.ymarketShowcaseMin || filters.ymarketShowcaseMax) {
    push(showcaseRangeLabel(filters.ymarketShowcaseMin, filters.ymarketShowcaseMax), without({ ymarketShowcaseMin: undefined, ymarketShowcaseMax: undefined }))
  }
  if (filters.stockMismatch) push('Остатки расходятся', without({ stockMismatch: undefined }))
  if (filters.missingAuthoritativeWeight) push('Нет веса', without({ missingAuthoritativeWeight: undefined }))
  if (filters.descriptionPresence) push(filters.descriptionPresence === 'absent' ? 'Нет описания' : 'Есть описание', without({ descriptionPresence: undefined }))
  if (filters.discrepancy) push(discrepancyChipLabels[filters.discrepancy], without({ discrepancy: undefined }))
  if (filters.binding) push(filters.binding === 'missing' ? MISSING_BINDING_LABEL : `Связь с площадкой: ${bindingLabels[filters.binding]}`, without({ binding: undefined }))
  if (filters.staleOnly) push(discrepancyChipLabels.stale_observation, without({ staleOnly: undefined }))
  if (filters.problems) push('Есть любая проблема', without({ problems: undefined }))
  // The backfill match of a saved link; the panel no longer offers it.
  if (filters.matchBucket) push(`Архивное сопоставление: ${matchBucketLabels[filters.matchBucket]}`, without({ matchBucket: undefined }))
  if (filters.missingTaxonomy) push(`Не заполнено: ${missingTaxonomyLabel(filters.missingTaxonomy, TaxonomyFactSchema.options.length)}`, without({ missingTaxonomy: undefined }))
  if (filters.missingMarking) push('Без маркировки', without({ missingMarking: undefined }))
  if (filters.unavailableOrStaleStock) push('Остаток 1С устарел или не записан', without({ unavailableOrStaleStock: undefined }))
  if (filters.rowGrain === 'merge_survived') push('Свёрнутые при объединении записи', without({ rowGrain: undefined }))
  for (const program of filters.orderProgram ?? []) {
    const remaining = (filters.orderProgram ?? []).filter((value) => value !== program)
    push(`Программа заказа: ${program}`, without({ orderProgram: remaining.length > 0 ? remaining : undefined }))
  }
  if (filters.orderCity) push(`Город заказа: ${filters.orderCity}`, without({ orderCity: undefined }))
  for (const status of filters.orderStatus ?? []) {
    const remaining = (filters.orderStatus ?? []).filter((value) => value !== status)
    push(`Статус заказа: ${STATUS_LABELS[status] ?? status}`, without({ orderStatus: remaining.length > 0 ? remaining : undefined }))
  }
  return chips
}

export function WarehouseFilterChips({ filters, onChange, lifecycle = 'all', onLifecycle, parkedNote = null }: Pick<Props, 'filters' | 'onChange'> & Partial<Pick<Props, 'providerSlots' | 'availableFilters' | 'lifecycle' | 'onLifecycle'>> & { parkedNote?: string | null }) {
  const conflict = statusScopeConflict(filters, lifecycle)
  const thresholds = useCheapThresholds(filters.ymarketBelowThreshold === true)
  const chips = warehouseFilterChips(filters, lifecycle, thresholds)
  if (chips.length === 0 && parkedNote === null) return null
  return (
    <div aria-label="Активные фильтры" className="mt-2 flex flex-wrap items-center gap-2">
      {conflict !== null ? <p role="note" className="basis-full text-xs text-st-unpaid">{conflict}</p> : null}
      {parkedNote !== null ? <p role="note" data-testid="parked-filters-note" className="basis-full text-xs text-ink-muted">{parkedNote}</p> : null}
      {chips.map((chip) => (
        <button
          key={chip.label}
          type="button"
          aria-label={`${chip.label} ×`}
          onClick={() => {
            if (chip.lifecycle) onLifecycle?.('all', filters)
            else onChange(chip.clear())
          }}
          className="rounded-full bg-bone px-3 py-1 text-xs text-ink-2"
        >
          {chip.label} ×
        </button>
      ))}
    </div>
  )
}
